import { useQuery } from "@tanstack/react-query"
import type {
  UseBaseQueryOptions,
  UseQueryResult,
  QueryKey,
  WithRequired,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type {
  FindIngestionErrorLogsQueryResponse,
  FindIngestionErrorLogsPathParams,
} from "../types/FindIngestionErrorLogs"

type FindIngestionErrorLogsClient = typeof client<
  FindIngestionErrorLogsQueryResponse,
  Error,
  never
>
type FindIngestionErrorLogs = {
  data: FindIngestionErrorLogsQueryResponse
  error: Error
  request: never
  pathParams: FindIngestionErrorLogsPathParams
  queryParams: never
  headerParams: never
  response: Awaited<ReturnType<FindIngestionErrorLogsClient>>
  client: {
    parameters: Partial<Parameters<FindIngestionErrorLogsClient>[0]>
    return: Awaited<ReturnType<FindIngestionErrorLogsClient>>
  }
}
export const findIngestionErrorLogsQueryKey = (
  ingestionId: FindIngestionErrorLogsPathParams["ingestionId"]
) =>
  [
    {
      url: "/v1/ingestions/find-ingestion-error-logs/:ingestionId",
      params: { ingestionId },
    },
  ] as const
export type FindIngestionErrorLogsQueryKey = ReturnType<
  typeof findIngestionErrorLogsQueryKey
>
export function findIngestionErrorLogsQueryOptions<
  TData = FindIngestionErrorLogs["response"],
  TQueryData = FindIngestionErrorLogs["response"],
>(
  ingestionId: FindIngestionErrorLogsPathParams["ingestionId"],
  options: FindIngestionErrorLogs["client"]["parameters"] = {}
): WithRequired<
  UseBaseQueryOptions<
    FindIngestionErrorLogs["response"],
    FindIngestionErrorLogs["error"],
    TData,
    TQueryData
  >,
  "queryKey"
> {
  const queryKey = findIngestionErrorLogsQueryKey(ingestionId)
  return {
    queryKey,
    queryFn: async () => {
      const res = await client<
        FindIngestionErrorLogs["data"],
        FindIngestionErrorLogs["error"]
      >({
        method: "get",
        url: `/v1/ingestions/find-ingestion-error-logs/${ingestionId}`,
        ...options,
      })
      return res
    },
  }
}
/**
 * @summary Find ingestion error logs
 * @link /v1/ingestions/find-ingestion-error-logs/:ingestionId
 */
export function useFindIngestionErrorLogs<
  TData = FindIngestionErrorLogs["response"],
  TQueryData = FindIngestionErrorLogs["response"],
  TQueryKey extends QueryKey = FindIngestionErrorLogsQueryKey,
>(
  ingestionId: FindIngestionErrorLogsPathParams["ingestionId"],
  options: {
    query?: Partial<
      UseBaseQueryOptions<
        FindIngestionErrorLogs["response"],
        FindIngestionErrorLogs["error"],
        TData,
        TQueryData,
        TQueryKey
      >
    >
    client?: FindIngestionErrorLogs["client"]["parameters"]
  } = {}
): UseQueryResult<TData, FindIngestionErrorLogs["error"]> & {
  queryKey: TQueryKey
} {
  const { query: queryOptions, client: clientOptions = {} } = options ?? {}
  const queryKey =
    queryOptions?.queryKey ?? findIngestionErrorLogsQueryKey(ingestionId)
  const query = useQuery<
    FindIngestionErrorLogs["data"],
    FindIngestionErrorLogs["error"],
    TData,
    any
  >({
    ...findIngestionErrorLogsQueryOptions<TData, TQueryData>(
      ingestionId,
      clientOptions
    ),
    queryKey,
    ...queryOptions,
  }) as UseQueryResult<TData, FindIngestionErrorLogs["error"]> & {
    queryKey: TQueryKey
  }
  query.queryKey = queryKey as TQueryKey
  return query
}
